<template>
  <div class="variable-picker">
    <!-- Search and Filter -->
    <div class="mb-3">
      <div class="row">
        <div class="col-md-6">
          <input
            v-model="searchTerm"
            type="text"
            class="form-control"
            placeholder="Search variables..."
          />
        </div>
        <div class="col-md-6">
          <select v-model="selectedCategory" class="form-select">
            <option value="">All Categories</option>
            <option
              v-for="category in categories"
              :key="category._id"
              :value="category._id"
            >
              {{ category.title }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Selected Variables Display -->
    <div v-if="selectedVariables.length > 0" class="mb-3">
      <h6 class="mb-2">Selected Variables:</h6>
      <div class="selected-variables">
        <span
          v-for="variable in selectedVariables"
          :key="variable._id"
          class="badge bg-primary me-2 mb-2 d-inline-flex align-items-center"
        >
          {{ variable.title }}
          <button
            type="button"
            class="btn-close btn-close-white ms-2"
            @click="removeVariable(variable)"
            aria-label="Remove"
          ></button>
        </span>
      </div>
    </div>

    <!-- Variables List -->
    <div class="variables-list" style="max-height: 300px; overflow-y: auto;">
      <div v-if="filteredVariables.length === 0" class="text-center text-muted py-3">
        No variables found
      </div>
      <div
        v-for="variable in filteredVariables"
        :key="variable._id"
        class="variable-item card mb-2"
        @click="toggleVariable(variable)"
        :class="{ 'selected': isSelected(variable._id) }"
        style="cursor: pointer;"
      >
        <div class="card-body p-3">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <h6 class="card-title mb-1">{{ variable.title }}</h6>
              <p class="card-text text-muted small mb-1">
                Category: {{ variable.category_title || 'N/A' }}
              </p>
              <p class="card-text small">{{ variable.description }}</p>
              <small class="text-info">Slug: <span v-text="variable.slug"></span></small>
            </div>
            <div class="form-check checkbox-wrapper">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="isSelected(variable._id)"
                @click.stop="toggleVariable(variable)"
              />
              <label class="form-check-label text-xs">
                Select
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import apiService from '@/services/apiService';

interface Variable {
  _id: string;
  title: string;
  description: string;
  slug: string;
  category_title?: string;
  prompt_category_id?: string;
}

interface Category {
  _id: string;
  title: string;
}

const props = defineProps<{
  modelValue: Variable[];
  currentCategoryId?: string;
  variables?: Variable[]; // Optional: pass variables from parent
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Variable[]): void;
}>();

// Local state
const searchTerm = ref('');
const selectedCategory = ref(props.currentCategoryId || '');
const availableVariables = ref<Variable[]>([]);
const categories = ref<Category[]>([]);
const selectedVariables = ref<Variable[]>([...props.modelValue]);

// Computed
const filteredVariables = computed(() => {
  let filtered = availableVariables.value;

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(v => v.prompt_category_id === selectedCategory.value);
  }

  // Filter by search term
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    filtered = filtered.filter(v => 
      (v.title?.toLowerCase().includes(term) || false) ||
      (v.description?.toLowerCase().includes(term) || false) ||
      (v.slug?.toLowerCase().includes(term) || false)
    );
  }

  return filtered;
});

// Methods
const isSelected = (variableId: string): boolean => {
  return selectedVariables.value.some(v => v._id === variableId);
};

const toggleVariable = (variable: Variable) => {
  console.log('🚀 toggleVariable called with:', {
    id: variable._id,
    title: variable.title,
    slug: variable.slug,
    fullVariable: variable
  });
  
  const index = selectedVariables.value.findIndex(v => v._id === variable._id);
  
  if (index > -1) {
    // Remove variable
    console.log('❌ Removing variable:', variable.title);
    selectedVariables.value.splice(index, 1);
  } else {
    // Add variable
    console.log('✅ Adding variable:', variable.title, 'with slug:', variable.slug);
    selectedVariables.value.push(variable);
  }
  
  console.log('📤 Emitting updated selectedVariables:', [...selectedVariables.value]);
  console.log('📤 Selected variables count:', selectedVariables.value.length);
  
  emit('update:modelValue', [...selectedVariables.value]);
};

const removeVariable = (variable: Variable) => {
  const index = selectedVariables.value.findIndex(v => v._id === variable._id);
  if (index > -1) {
    selectedVariables.value.splice(index, 1);
    emit('update:modelValue', [...selectedVariables.value]);
  }
};

// Variables are automatically added to base description when selected via watchers

// Load data
const loadVariables = async () => {
  if (props.variables && props.variables.length > 0) {
    console.log('📦 Using variables from props:', props.variables.length);
    availableVariables.value = props.variables.filter(item => item && typeof item === 'object').map(item => ({
      _id: item._id,
      title: item.title || '',
      description: item.description || '',
      slug: item.slug || '',
      category_title: (item.prompt_category_id && typeof item.prompt_category_id === 'object') 
        ? (item.prompt_category_id as any).title 
        : undefined,
      prompt_category_id: (item.prompt_category_id && typeof item.prompt_category_id === 'object')
        ? (item.prompt_category_id as any)._id
        : item.prompt_category_id
    }));
    return;
  }

  try {
    console.log('📡 Fetching variables from API...');
    const response = await apiService.get('/common/prompt-variables');
    // Handle both wrapped {.data: [...]} and direct array responses
    const data = (response && response.data && Array.isArray(response.data)) 
      ? response.data 
      : (Array.isArray(response) ? response : []);
      
    if (Array.isArray(data)) {
      const validData = data.filter(item => item && typeof item === 'object');
      console.log('✅ Fetched variables count:', validData.length);
      availableVariables.value = validData.map(item => ({
        _id: item._id,
        title: item.title || '',
        description: item.description || '',
        slug: item.slug || '',
        category_title: (item.prompt_category_id && typeof item.prompt_category_id === 'object') 
          ? (item.prompt_category_id as any).title 
          : undefined,
        prompt_category_id: (item.prompt_category_id && typeof item.prompt_category_id === 'object')
          ? (item.prompt_category_id as any)._id
          : item.prompt_category_id
      }));
    } else {
      console.warn('⚠️ API returned no variables or invalid format');
    }
  } catch (error) {
    console.error('❌ Error loading variables:', error);
  }
};

const loadCategories = async () => {
  try {
    const response = await apiService.get('/common/prompt-categories');
    // Handle both wrapped {.data: [...]} and direct array responses
    const data = (response && response.data && Array.isArray(response.data)) 
      ? response.data 
      : (Array.isArray(response) ? response : []);

    if (Array.isArray(data)) {
      categories.value = data.filter(item => item && typeof item === 'object');
    }
  } catch (error) {
    console.error('Error loading categories:', error);
  }
};

// Watch for changes in props
watch(() => props.modelValue, (newValue) => {
  selectedVariables.value = [...newValue];
}, { deep: true });

// Emit selection changes for debugging
watch(() => selectedVariables.value, (newValue, oldValue) => {
  console.log('VariablePicker - selectedVariables changed:', { newValue, oldValue });
}, { deep: true });

watch(() => props.variables, (newValue) => {
  if (newValue) loadVariables();
}, { deep: true });

watch(() => props.currentCategoryId, (newValue) => {
  selectedCategory.value = newValue || '';
});

// Initialize
onMounted(async () => {
  await Promise.all([loadVariables(), loadCategories()]);
});
</script>

<style scoped>
.variable-item {
  transition: all 0.2s ease;
}

.variable-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.variable-item.selected { 
  background-color: #d7e4e6;
  border: 2px solid #35bfbf;
}
.variable-item{
   background-color: #f0f7f8;
   border: 2px solid transparent;
}

.selected-variables .badge {
  font-size: 0.85em;
}

.btn-close-white {
  filter: brightness(0) invert(1);
  font-size: 0.7em;
}

/* Hide the native checkbox */
.checkbox-wrapper input[type="checkbox"] {
  display: none;
}

/* Style the label as a custom checkbox */
.checkbox-wrapper label {
  position: relative;
  padding-left: 30px; /* space for the box */
  cursor: pointer;
  user-select: none;
}

/* Create the box */
.checkbox-wrapper label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 20px;
  height: 20px;
  border: 2px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  transition: all 0.2s ease;
}

/* Checkmark (hidden by default) */
.checkbox-wrapper label::after {
  content: '';
  position: absolute;
  left: 7px;
  top: 3px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: all 0.2s ease;
}

/* When checked */
.checkbox-wrapper input[type="checkbox"]:checked + label::before {
  background-color: #35bfbf; /* your color */
  border-color: #35bfbf;
}

.checkbox-wrapper input[type="checkbox"]:checked + label::after {
  opacity: 1;
}

</style>